/**
 * Authentication service for handling login, logout, and token management
 */

import axios from "axios";
import { getApiUrl, buildApiPath, API_ENDPOINTS } from "../../../shared/utils/apiConfig";

// Token storage keys
const ACCESS_TOKEN_KEY = "access_token";
const USER_DATA_KEY = "user_data";
const AXIOS_TIMEOUT_MS = 5 * 60 * 1000;

class AuthService {
  constructor() {
    this.axios = axios.create({
      baseURL: getApiUrl(),
      timeout: AXIOS_TIMEOUT_MS,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to include auth token
    this.axios.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Add response interceptor to handle token expiration
    this.axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Skip retry for auth endpoints to prevent loops
        const isAuthEndpoint = originalRequest.url?.includes("/auth/");

        // If token is expired and we haven't already tried to refresh
        // and this isn't an auth endpoint (to prevent loops)
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            await this.refreshToken();

            // Retry the original request with new token
            const token = this.getAccessToken();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return this.axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            this.logout();
            // Trigger app to redirect to login
            window.dispatchEvent(new CustomEvent("auth-logout"));
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login response with user data and tokens
   */
  async login(email, password) {
    try {
      const response = await this.axios.post(
        buildApiPath(API_ENDPOINTS.AUTH.LOGIN),
        {
          email,
          password,
        },
      );

      const { access_token, user } = response.data;

      // Store tokens and user data
      this.setAccessToken(access_token);
      this.setUserData(user);

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Logout user and clear stored data
   */
  async logout() {
    try {
      // Call logout API to invalidate server-side session
      const token = this.getAccessToken();
      if (token) {
        await this.axios.post(buildApiPath(API_ENDPOINTS.AUTH.LOGOUT));
      }
    } catch (error) {
      console.warn("Logout API call failed:", error);
      // Continue with client-side cleanup even if API call fails
    }

    // Clear local storage
    this.clearTokens();
    this.clearUserData();
  }

  /**
   * Refresh access token using refresh token (handled by httpOnly cookie)
   */
  async refreshToken(options = {}) {
    const { silent = false } = options;
    try {
      const response = await this.axios.post(
        buildApiPath(API_ENDPOINTS.AUTH.REFRESH),
      );
      const { access_token, user } = response.data;

      this.setAccessToken(access_token);
      this.setUserData(user);

      return response.data;
    } catch (error) {
      if (!silent) {
        console.error("Token refresh error:", error);
      }
      throw error;
    }
  }

  /**
   * Get current user data from API
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    try {
      const response = await this.axios.get(
        buildApiPath(API_ENDPOINTS.AUTH.ME),
      );
      const user = response.data;

      // Update stored user data
      this.setUserData(user);

      return user;
    } catch (error) {
      console.error("Get current user error:", error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Success response
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await this.axios.put(
        buildApiPath(API_ENDPOINTS.AUTH.PASSWORD),
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Change password error:", error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Check if user is currently authenticated
   * @returns {boolean} True if user has valid token
   */
  isAuthenticated() {
    const token = this.getAccessToken();
    const userData = this.getUserData();
    return !!(token && userData);
  }

  /**
   * Get stored access token
   * @returns {string|null} Access token or null
   */
  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Set access token in storage
   * @param {string} token - Access token to store
   */
  setAccessToken(token) {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }

  /**
   * Get stored user data
   * @returns {Object|null} User data or null
   */
  getUserData() {
    const userData = localStorage.getItem(USER_DATA_KEY);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error("Error parsing user data:", error);
        return null;
      }
    }
    return null;
  }

  /**
   * Set user data in storage
   * @param {Object} userData - User data to store
   */
  setUserData(userData) {
    if (userData) {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(USER_DATA_KEY);
    }
  }

  /**
   * Clear all stored tokens
   */
  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Clear stored user data
   */
  clearUserData() {
    localStorage.removeItem(USER_DATA_KEY);
  }

  /**
   * Handle authentication errors and convert to user-friendly messages
   * @param {Error} error - Error from API call
   * @returns {Error} Processed error with user-friendly message
   */
  handleAuthError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          return new Error("Invalid email or password");
        case 403:
          return new Error("Access denied. Please contact your administrator.");
        case 422:
          return new Error("Please check your input and try again");
        case 429:
          return new Error("Too many attempts. Please try again later");
        case 500:
          return new Error("Server error. Please try again later");
        default:
          return new Error(
            data?.detail ||
              data?.message ||
              "An error occurred during authentication",
          );
      }
    } else if (error.request) {
      return new Error("Network error. Please check your connection");
    } else {
      return new Error("An unexpected error occurred");
    }
  }

  /**
   * Get axios instance for making authenticated requests
   * @returns {AxiosInstance} Configured axios instance
   */
  getAxios() {
    return this.axios;
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;
